
import { Component, Vue } from 'vue-property-decorator';
import { SettingsApiUiClient, SettingItem, FileParameter  } from '../../backend/backendapi';
import { initClient } from '../../../api';
import Spinner from '../../util/Spinner.vue';
import HelpPopover from './HelpPopover.vue';
import SettingImageUploader from './SettingImageUploader.vue';
import { SettingType } from './types';

@Component({
  components: {
    HelpPopover,
    Spinner,
    SettingImageUploader
  }
})
export default class Tab1 extends Vue {
  // Enums exposed to template
  readonly SettingType = SettingType;

  // State
  settings: SettingItem[] = [];
  expandedCategories: Record<string, boolean> = {};
  expandedPreviews: Record<string, boolean> = {};
  activeUploadsCount = 0;

  readonly service = initClient(SettingsApiUiClient);

  mounted(): void {
    this.fetchSettings();
  }

  get isAnyUploading(): boolean {
    return this.activeUploadsCount > 0;
  }

get groupedSettings(): Record<string, SettingItem[]> {
  // Sort settings alphabetically by category name first
  const sorted = [...this.settings].sort((a, b) => {
    const catA = a.category || 'General';
    const catB = b.category || 'General';
    return catA.localeCompare(catB);
  });

  // Group them into the record object
  return sorted.reduce((acc, item) => {
    const cat = item.category || 'General';
    if (!acc[cat]) {
      acc[cat] = [];
    }
    acc[cat].push(item);
    return acc;
  }, {} as Record<string, SettingItem[]>);
}

  getInputColumnClass(type: SettingType): string {
    switch (type) {
      case SettingType.Html: return 'col-12 mt-2';
      case SettingType.Image: return 'col-md-7';
      default: return 'col-md-3';
    }
  }

  async fetchSettings(): Promise<void> {
    try {
      const data = await this.service.list();
      this.settings = data;
      
      // collapse all categories by default using Vue.set for reactivity
      const initialCategories: Record<string, boolean> = {};
      data.forEach(item => {
        const cat = item.category || 'General';
        if (initialCategories[cat] === undefined) {
          initialCategories[cat] = false;
        }
      });
      this.expandedCategories = initialCategories;
    } catch (e) {
      this.$toasted.error(this.$t('txtError').toString());
    }
  }

  async postSettings(): Promise<void> {
    if (this.isAnyUploading) {
      this.$toasted.error('Please wait for uploads to complete');
      return;
    }

    try {
      await this.service.update(this.settings);
      this.$toasted.info(this.$t('txtDone').toString());
    } catch (e) {
      this.$toasted.error(this.$t('txtError').toString());
    }
  }

  toggleCategory(category: string): void {
    this.$set(this.expandedCategories, category, !this.expandedCategories[category]);
  }

  togglePreview(id: string): void {
    this.$set(this.expandedPreviews, id, !this.expandedPreviews[id]);
  }

  handleUploadStatusChange(isUploading: boolean): void {
    if (isUploading) {
      this.activeUploadsCount++;
    } else {
      this.activeUploadsCount = Math.max(0, this.activeUploadsCount - 1);
    }
  }

  async onExcelSelected(event: Event, item: SettingItem): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
 
    this.activeUploadsCount++;
    try {
      input.value = '';

      const upload: FileParameter = {
        data: file,
        fileName: file.name
      };
 
      await this.service.importExcel(
        item.internalId,
        item.id ?? null,
        item.parentId ?? null,
        [upload]
      );
 
      this.$toasted.info('Excel imported successfully.');
    } catch (e) {
      this.$toasted.error(this.$t('txtError').toString());
    } finally {
      this.activeUploadsCount = Math.max(0, this.activeUploadsCount - 1);
    }
  }
}
